import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  IGetTrainingsResponse,
  ITraining,
  ITrainingCount,
  ITrainingMutationData,
  ITrainingState,
} from "../types/training";

const initialState: ITrainingState = {
  trainings: [],
  training: null,
  counts: {
    total_training: 0,
    total_inactive_training: 0,
    total_active_training: 0,
  },
  total: 0,
  month_total: 0,
  limit: 10,
  offset: 0,
  loading: false,
  loadingTrainingId: null,
};

const toTraining = (
  training: ITrainingMutationData,
  existing?: ITraining,
): ITraining => ({
  id: String(training.id),
  name: training.name,
  amount: training.amount,
  body: training.body,
  address: training.address,
  image: training.image,
  status: existing?.status ?? "",
  duration: training.duration,
  date: new Date(training.date),
  link: training.link,
  country: { id: training.country_id },
  state: { id: training.state_id },
});

const trainingSlice = createSlice({
  name: "training",
  initialState,
  reducers: {
    setTrainings: (state, action: PayloadAction<IGetTrainingsResponse>) => {
      state.trainings = action.payload.data.data;
      state.limit = action.payload.data.limit;
      state.offset = action.payload.data.offset;
      state.total = action.payload.data.total;
    },
    setTraining: (
      state,
      action: PayloadAction<ITraining | ITrainingMutationData>,
    ) => {
      state.training =
        "country_id" in action.payload
          ? toTraining(
              action.payload,
              state.trainings.find(
                (item) => item.id === String(action.payload.id),
              ),
            )
          : action.payload;
    },
    createTraining: (state, action: PayloadAction<ITrainingMutationData>) => {
      const createdTraining = toTraining(action.payload);
      if (state.offset === 0) {
        state.trainings = [
          createdTraining,
          ...state.trainings.filter((item) => item.id !== createdTraining.id),
        ].slice(0, state.limit);
      }
      state.total += 1;
    },
    editTraining: (state, action: PayloadAction<ITrainingMutationData>) => {
      const existing = state.trainings.find(
        (item) => item.id === String(action.payload.id),
      );
      const updatedTraining = toTraining(action.payload, existing);
      state.trainings = state.trainings.map((item) =>
        item.id === updatedTraining.id ? updatedTraining : item,
      );
      if (String(state.training?.id) === updatedTraining.id) {
        state.training = updatedTraining;
      }
    },
    deleteTraining: (state, action: PayloadAction<string | number>) => {
      const id = String(action.payload);
      state.trainings = state.trainings.filter((item) => item.id !== id);
      state.total = Math.max(0, state.total - 1);
      if (String(state.training?.id) === id) {
        state.training = null;
      }
    },
    setTrainingLoading: (state) => {
      state.loading = true;
    },
    disableTrainingLoading: (state) => {
      state.loading = false;
    },
    setLoadingTrainingId: (state, action: PayloadAction<string | null>) => {
      state.loadingTrainingId = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.limit = action.payload;
    },
    setOffset: (state, action: PayloadAction<number>) => {
      state.offset = action.payload;
    },
    setTotalTraining: (
      state,
      action: PayloadAction<ITrainingCount["data"]>,
    ) => {
      state.counts = action.payload;
    },
    setMonthTotal: (state, action: PayloadAction<number>) => {
      state.month_total = action.payload;
    },
    clearTraining: (state) => {
      state.training = null;
    },
  },
});

export const {
  setTrainings,
  setTraining,
  createTraining,
  editTraining,
  deleteTraining,
  setTrainingLoading,
  disableTrainingLoading,
  setLoadingTrainingId,
  setLimit,
  setOffset,
  setTotalTraining,
  setMonthTotal,
  clearTraining,
} = trainingSlice.actions;

export default trainingSlice.reducer;
